import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useIsAdmin } from '../auth/AuthContext'

/**
 * Header banner shown only while *e2e_scheduler itself* is behind on fax processing.
 *
 * THE WORDING ATTRIBUTES THE DELAY TO US, NEVER TO THE FAX SERVICE (G4.3). This is the
 * same distinction `fault_domain` draws, and it is the entire point of the indicator: an
 * operator watching a slow dashboard must not conclude eFax is slow when the queue is
 * ours. Every string below therefore names e2e_scheduler as the subject.
 *
 * IT COMPUTES NOTHING (G4.14). The 2-minute average, the threshold comparison, the
 * enabled flag and the readability of the signal are all decided by
 * `GET /api/system/pressure`. This component branches on one boolean, `over_threshold`,
 * and renders the numbers the server already produced. That is what keeps the client
 * cost to one small request per tab per 30 s and keeps two browser tabs from disagreeing
 * about whether the app is under pressure.
 *
 * IT RENDERS `null`, NOT AN EMPTY BOX (G4.2). No placeholder, no grey chip, no reserved
 * height. A zero-height element that later grows would shift the header down at the
 * moment the operator is least able to tolerate the page moving, so the element does not
 * exist until it has something to say.
 *
 * `readable: false` RENDERS NOTHING EITHER (G4.10). An unreadable CPU signal is not
 * evidence of pressure. The server already folds that into `over_threshold`, logs the
 * condition once, and surfaces it on Settings -> System — never here. This mirrors
 * `inbound_pending_durable_readable`, which exists precisely because a failed read and an
 * idle pipeline otherwise look identical.
 *
 * DISTINGUISHABLE WITHOUT COLOUR (G4.11's sibling requirement). The warning carries a
 * glyph and the literal word "Delayed" alongside the tint, so it reads the same to a user
 * who cannot distinguish the amber. Colours are declared for BOTH foreground and
 * background on the banner itself rather than inheriting the page's, so its contrast
 * ratio (~8:1, well past WCAG AA's 4.5:1 for body text) holds whatever theme surrounds
 * it — the app ships a dark palette today and a light one must not silently break it.
 */

/** Fixed poll interval. Not adaptive: a variable interval would make the cost of the
 *  feature depend on the pressure it reports, which is the coupling G4.6 forbids. */
const POLL_MS = 30_000

interface PressureResponse {
  average_pct: number | null
  threshold_pct: number
  window_seconds: number
  sample_count: number
  readable: boolean
  over_threshold: boolean
  enabled: boolean
  backlog?: {
    ocr_backlog?: number
    inbound_backlog?: number
    inbound_pending_durable?: number | null
    inbound_pending_durable_readable?: boolean | null
  }
}

const BANNER_BG = '#5c3a00'
const BANNER_FG = '#ffe0b2'
const BANNER_EDGE = '#ffb74d'

function minutesLabel(seconds: number): string {
  if (seconds % 60 === 0) {
    const m = seconds / 60
    return m === 1 ? '1 minute' : `${m} minutes`
  }
  return `${seconds} seconds`
}

export function PressureBanner() {
  const [data, setData] = useState<PressureResponse | null>(null)
  const isAdmin = useIsAdmin()
  // Guards against a slow response overlapping the next tick and against setting state
  // after unmount, which would warn in dev and leak a timer in production.
  const inFlight = useRef(false)

  useEffect(() => {
    let cancelled = false

    const poll = async () => {
      if (inFlight.current) return
      inFlight.current = true
      try {
        const res = await fetch('/api/system/pressure', { credentials: 'same-origin' })
        if (!res.ok) {
          // A failed poll is NOT evidence of pressure, so it renders nothing rather than
          // a warning or an error strip. Same reasoning as readable:false.
          if (!cancelled) setData(null)
          return
        }
        const body = (await res.json()) as PressureResponse
        if (!cancelled) setData(body)
      } catch {
        if (!cancelled) setData(null)
      } finally {
        inFlight.current = false
      }
    }

    poll()
    const timer = window.setInterval(poll, POLL_MS)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [])

  // The ONLY branch. Disabled, unreadable, below threshold and not-yet-enough-samples are
  // all already folded into this one server-side boolean.
  if (!data || !data.over_threshold) return null

  const avg = data.average_pct === null ? null : Math.round(data.average_pct)
  const ocr = data.backlog?.ocr_backlog
  const inbound = data.backlog?.inbound_backlog

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        background: BANNER_BG,
        color: BANNER_FG,
        borderBottom: `1px solid ${BANNER_EDGE}`,
        borderLeft: `4px solid ${BANNER_EDGE}`,
        padding: '0.6rem 2rem',
        fontSize: '0.85rem',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '0.6rem',
      }}
    >
      {/* Decorative: the text below already says "Delayed", so the glyph is not the only
          carrier of meaning and must not be announced twice by a screen reader. */}
      <span aria-hidden="true" style={{ fontSize: '1rem', lineHeight: 1.3 }}>
        ⚠
      </span>
      <div>
        <strong style={{ color: '#ffffff' }}>
          Delayed — e2e_scheduler is behind on fax processing.
        </strong>{' '}
        <span>
          Our own CPU has averaged{' '}
          {avg === null ? 'above the threshold' : `${avg}%`} of this container&rsquo;s quota
          over the last {minutesLabel(data.window_seconds)} (threshold{' '}
          {data.threshold_pct}%). Sends, inbound OCR and status polling are queueing here,
          not at the fax service.
        </span>
        <div style={{ marginTop: '0.3rem', opacity: 0.95 }}>
          Nothing is lost — measurements settle once the queue drains. Wait for it to clear
          rather than launching another batch, which would add to the same queue.
          {(ocr !== undefined || inbound !== undefined) && (
            <>
              {' '}
              Queued on this worker: {ocr ?? '—'} awaiting OCR, {inbound ?? '—'} awaiting
              download.
            </>
          )}
          {isAdmin && (
            <>
              {' '}
              <Link to="/settings/system" style={{ color: '#ffffff', fontWeight: 600 }}>
                Backlog detail and thresholds
              </Link>
              {' · '}
              <Link to="/settings/metrics" style={{ color: '#ffffff', fontWeight: 600 }}>
                Metrics
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default PressureBanner
